import { IS_SERVER } from 'config';
import { captureErrorInSentry } from 'lib/errors';

const SUPPORTS_SESSION_STORAGE = typeof window !== 'undefined' && !IS_SERVER && !!window?.sessionStorage?.getItem;

const safe =
  (cb) =>
  (...args) => {
    try {
      if (SUPPORTS_SESSION_STORAGE) return cb(...args);
    } catch (error) {
      captureErrorInSentry(error);
    }
  };

const unsafeSet = (key, value) => {
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  }
};

const unsafeGet = (key: string) => {
  try {
    if (typeof window !== 'undefined') {
      const item = window.sessionStorage.getItem(key);

      const protoCheck = (key: string, value: unknown) => {
        if (key === '__proto__') {
          return undefined;
        }
        return value;
      };

      return item ? JSON.parse(item, protoCheck) : undefined;
    }
  } catch (error) {
    captureErrorInSentry(error);
    return undefined;
  }
};

const unsafeMerge = (key, updates) => {
  unsafeSet(key, { ...unsafeGet(key), ...updates });
};

const unsafeRemove = (key) => {
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(key);
  }
};

const unsafeClear = () => {
  if (typeof window !== 'undefined') {
    window.sessionStorage.clear();
  }
};

export const clear = safe(unsafeClear);

export const get = safe(unsafeGet);

export const set = safe(unsafeSet);

export const merge = safe(unsafeMerge);

export const remove = safe(unsafeRemove);
